import { Bug, Copy as CopyIcon } from 'iconoir-react'
import { type ReactNode, useState } from 'react'
import type {
  AiConversationTurn,
  AiConversationTurnStep,
  AiConversationTurnToolCall,
  AiConversationTurnToolResult,
} from 'app/generated/graphql'
import { copyToClipboard } from 'app/hooks/useCopyToClipboard'

/** Pretty-prints an arbitrary JSON scalar (tool args/results) into a scrollable block. */
function JsonBlock({ value }: { value: unknown }) {
  if (value == null) return <div className="text-label-xs text-minimal">—</div>
  let text: string
  try {
    text = typeof value === `string` ? value : JSON.stringify(value, null, 2)
    // oxlint-disable-next-line local/require-catch-logging -- fall back to String() on circular/unserializable values
  } catch {
    text = String(value)
  }
  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Copy to clipboard"
        onClick={() => copyToClipboard(text)}
        className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded bg-inverse/80 text-minimal transition-colors hover:bg-inverse hover:text-inverse"
      >
        <CopyIcon className="h-3.5 w-3.5" />
      </button>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-inverse px-2 py-1.5 pr-9 text-label-xs leading-relaxed text-inverse">
        {text}
      </pre>
    </div>
  )
}

function ToolCallBlock({ call }: { call: AiConversationTurnToolCall }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 text-label-xs font-semibold text-blue-700">
        <span aria-hidden>→</span>
        <span>{call.toolName}</span>
        <span className="font-normal text-minimal">call</span>
      </div>
      <JsonBlock value={call.args} />
    </div>
  )
}

function ToolResultBlock({ result }: { result: AiConversationTurnToolResult }) {
  return (
    <div className="flex flex-col gap-1">
      <div
        className={`flex items-center gap-1 text-label-xs font-semibold ${
          result.isError ? `text-red-600` : `text-emerald-700`
        }`}
      >
        <span aria-hidden>←</span>
        <span>{result.toolName}</span>
        <span className="font-normal text-minimal">{result.isError ? `error` : `result`}</span>
      </div>
      <JsonBlock value={result.result} />
    </div>
  )
}

function StepBlock({ step }: { step: AiConversationTurnStep }) {
  const toolCalls = step.toolCalls ?? []
  const toolResults = step.toolResults ?? []
  return (
    <div className="flex flex-col gap-2">
      {step.text && <div className="whitespace-pre-wrap break-words text-label-xs italic text-subtle">{step.text}</div>}
      {toolCalls.map((call, i) => (
        <ToolCallBlock key={`call-${i}`} call={call} />
      ))}
      {toolResults.map((result, i) => (
        <ToolResultBlock key={`result-${i}`} result={result} />
      ))}
    </div>
  )
}

function formatLatency(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`
}

/**
 * The turn's LLM metadata (model, temperature, latency, tokens) plus a collapsed
 * system-prompt toggle, rendered as dot-separated plain text so it reads like
 * the message timestamp it sits beside — e.g. `gpt-4.1 model · 0 temp`. Renders
 * a short explainer when the turn had no LLM call. Meant to sit inside a
 * flex-wrap row — the expanded prompt wraps to its own full-width line.
 */
export function TurnMetaChips({ turn }: { turn: AiConversationTurn }) {
  const [showPrompt, setShowPrompt] = useState(false)
  // A turn with no model/latency was a templated send (initial outreach), not an
  // LLM inference — surface that so an empty meta row reads as expected, not broken.
  const hasLlmMeta = turn.model != null || turn.latencyMs != null || turn.temperature != null
  const parts: { key: string; node: ReactNode }[] = []
  const pushMeta = (key: string, value: string, label: string) =>
    parts.push({
      key,
      node: (
        <span>
          {value} <span className="text-minimal">{label}</span>
        </span>
      ),
    })
  if (turn.model) pushMeta(`model`, turn.model, `model`)
  if (turn.temperature != null) pushMeta(`temp`, String(turn.temperature), `temp`)
  if (turn.latencyMs != null) pushMeta(`latency`, formatLatency(turn.latencyMs), `latency`)
  if (turn.tokenUsage?.totalTokens != null) pushMeta(`tokens`, String(turn.tokenUsage.totalTokens), `tokens`)
  if (turn.tokenUsage?.cachedInputTokens) pushMeta(`cached`, String(turn.tokenUsage.cachedInputTokens), `cached tokens`)
  if (!hasLlmMeta) {
    parts.push({
      key: `no-llm`,
      node: (
        <span className="italic">{turn.isInitialMessage ? `templated send · no LLM call` : `no LLM metadata`}</span>
      ),
    })
  }
  if (turn.systemPrompt) {
    parts.push({
      key: `prompt`,
      node: (
        <button type="button" onClick={() => setShowPrompt((prev) => !prev)} className="font-medium hover:underline">
          System Prompt
        </button>
      ),
    })
  }

  return (
    <>
      {parts.map((part, i) => (
        <span key={part.key} className="flex items-center gap-x-1">
          {i > 0 && <span aria-hidden>·</span>}
          {part.node}
        </span>
      ))}
      {showPrompt && turn.systemPrompt && (
        <div className="order-last basis-full">
          <JsonBlock value={turn.systemPrompt} />
        </div>
      )}
    </>
  )
}

/** True when any step in the turn made a tool call or received a tool result. */
export function turnHasToolActivity(turn: AiConversationTurn): boolean {
  return (turn.steps ?? []).some((step) => (step.toolCalls?.length ?? 0) > 0 || (step.toolResults?.length ?? 0) > 0)
}

/**
 * Renders a tool-using AI agent turn as a right-aligned bubble, mirroring an
 * agent message (bubble on the right, meta row beneath) but labeled "Debug"
 * instead of "AI Agent" so it reads as a debug artifact. Surfaces the turn's
 * tool calls and results front-and-center, with a collapsed system prompt.
 * Regular (tool-free) turns are represented only by inline meta hints next to
 * the message timestamp — the caller decides when to render this. Pass
 * `hideMeta` when the turn's meta already renders inline by the message
 * timestamp, so the bubble's own row shows just the "Debug" label.
 * Super-user only.
 */
export function DebugTraceCard({
  turn,
  hideMeta,
  fullWidth,
}: {
  turn: AiConversationTurn
  hideMeta?: boolean
  /** Fill the container width (matching the agent turn) instead of max-w-[75%]. */
  fullWidth?: boolean
}) {
  // Only the tool-using steps belong in the bubble; the final text-only step
  // just restates the message and is dropped. Meta (model/temp/tokens, system
  // prompt) renders inline by the message timestamp for linked turns.
  const toolSteps = (turn.steps ?? []).filter(
    (step) => (step.toolCalls?.length ?? 0) > 0 || (step.toolResults?.length ?? 0) > 0,
  )
  return (
    <div className="group flex w-full flex-col gap-y-1 pt-1">
      <div className={fullWidth ? `flex` : `flex justify-end`}>
        <div
          className={`relative flex flex-col gap-2 rounded-2xl rounded-tr-md rounded-br-md border border-amber-400 bg-amber-100 p-3 font-mono ${
            fullWidth ? `w-full` : `max-w-[75%]`
          }`}
        >
          <span
            aria-hidden
            className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-surface ring-1 ring-amber-300"
          >
            <Bug className="h-3 w-3 text-amber-700" />
          </span>
          {toolSteps.length === 0 ? (
            <div className="text-label-xs text-amber-800">No tool activity recorded for this turn.</div>
          ) : (
            toolSteps.map((step, i) => <StepBlock key={i} step={step} />)
          )}
        </div>
      </div>
      {/* Linked turns show their meta inline by the message timestamp, so the
          label/meta row is only rendered for unlinked turns that have no message. */}
      {!hideMeta && (
        <div className="flex flex-wrap items-center justify-end gap-x-1 gap-y-0.5 px-1 text-label-xs text-subtle">
          <span className="font-medium text-amber-700">Debug</span>
          {turn.turnIndex != null && (
            <>
              <span aria-hidden>·</span>
              <span>turn {turn.turnIndex}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <TurnMetaChips turn={turn} />
        </div>
      )}
    </div>
  )
}
